import { useTranslation } from "react-i18next";
import {  useSelector} from "react-redux";
import { Card, Col, Row, Spinner } from "react-bootstrap";
import { RootState } from "../../../../redux/store";
import { AuditActionEnum } from "../../../auditLogs/types";
import { generateCsvFile } from "../../../../utils/downloadCsvFile";
import "./ImprovementPlanIndicators.css"
import ChartShareActions from "../../../../components/ChartShareActions";
import { useEffect, useRef } from "react";
import { ImprovementPlanIndicatorsDto } from "../../types";
import { useImprovementPlanIndicators } from "../../hooks/useImprovementPlanIndicators";
import { formatCurrency } from "../../../../utils/formatters/CurrencyFormatter";

interface ImprovementPlanIndicatorsProps {
    setMessage: (msg: string) => void;
}
export function ImprovementPlanIndicators({ setMessage }: ImprovementPlanIndicatorsProps) {
    const { t } = useTranslation()
    const { data, isLoading, error } = useImprovementPlanIndicators();

    useEffect(() => {
        if (error) {
            const messageError = (error as any)?.response?.data?.message || (error as Error).message;
            setMessage(messageError);
        }
    }, [error, setMessage]);
    const indicatorTimeTrackingRef = useRef<HTMLDivElement>(null);
    const selectedFilters = useSelector((state: RootState) => state.filters.improvementPlans.selectedFilters);

    const exportToCSV = async (): Promise<void> => {
        const rows: ImprovementPlanIndicatorsDto[] = data ? [data] : [];
        await generateCsvFile({
            headers: [
                t("improvementPlan:indicators.labelTotalCount"),
                t("improvementPlan:indicators.labelPercentageImprovementPlansFinished"),
                t("improvementPlan:indicators.labelPercentageImprovementPlansActionsFinished"),
                t("improvementPlan:indicators.labelPercentageImprovementPlansDemandsClosed"),
                t("improvementPlan:indicators.labelTotalFinalInverstment")
            ],
            rows,
            mapRow: (item) => [
                item.totalCount ?? 0,
                item.percentageImprovementPlansFinished ?? 0,
                item.percentageImprovementPlansActionsFinished ?? 0,
                item.percentageImprovementPlansDemandsClosed ?? 0,
                item.totalFinalInverstment ?? 0
            ],
            fileBaseName: "improvemnt_plans_indicators",
            trailingSemicolon: true,
            audit: {
                entity: "DashboardIndicator",
                action: AuditActionEnum.ExportCsv,
                source: window.location.pathname,
                description: "CSV export of improvement plans indicators",
                filters: selectedFilters
            },

        });
    };

    return (
        <>
            { isLoading && (
                <div className="overlay-spinner">
                    <Spinner animation="border" variant="light" />
                </div>
            )}
            <div ref={indicatorTimeTrackingRef}>
                <Card className="p-3 main-indicator-card mt-2">
                    <Row className="align-items-center justify-content-between mb-3 w-100">
                        <Col xs="auto">
                            <h4 className="mb-0">
                                {t("claim:indicatorsTimeTracking.title")}
                            </h4>
                        </Col>

                        <Col xs="auto">
                            <ChartShareActions
                                chartRef={indicatorTimeTrackingRef}
                                title={t("claim:indicatorsTimeTracking.title")}
                                filters={selectedFilters}
                                t={t}
                                exportCsvAction={exportToCSV}
                            />
                        </Col>
                    </Row>
                    <Row className="g-3 w-100">
                        <Col>
                            <Card className="h-100 border-0 shadow-sm">
                                <div className="text-start">
                                    <div className="indicator-bar-deep-blue-horizontal" />

                                    <p className="ml-3 mt-2 text-muted mb-1 small">
                                        {t("improvementPlan:indicators.labelTotalCount")}
                                    </p>

                                    <h4 className="ml-3  mb-2 fw-bold">
                                        {data?.totalCount || "-"}
                                    </h4>
                                </div>
                            </Card>
                        </Col>

                        <Col>
                            <Card className="h-100 border-0 shadow-sm">
                                <div className="text-start">
                                    <div className="indicator-bar-green-horizontal" />

                                    <p className="ml-3 mt-2 text-muted mb-1 small">
                                        {t("improvementPlan:indicators.labelPercentageImprovementPlansFinished")}
                                    </p>

                                    <h4 className="ml-3  mb-2 fw-bold">
                                        {data?.percentageImprovementPlansFinished || "-"} %
                                    </h4>
                                </div>
                            </Card>
                        </Col>

                        <Col>
                            <Card className="h-100 border-0 shadow-sm">
                                <div className="text-start">
                                    <div className="indicator-bar-purple-blue-horizontal" />

                                    <p className="ml-3 mt-2 text-muted mb-1 small">
                                        {t("improvementPlan:indicators.labelPercentageImprovementPlansActionsFinished")}
                                    </p>

                                    <h4 className="ml-3  mb-2 fw-bold">
                                        {data?.percentageImprovementPlansActionsFinished || "-"} %
                                    </h4>
                                </div>
                            </Card>
                        </Col>
                        <Col>
                            <Card className="h-100 border-0 shadow-sm">
                                <div className="text-start">
                                    <div className="indicator-bar-lavender-horizontal" />

                                    <p className="ml-3 mt-2 text-muted mb-1 small">
                                        {t("improvementPlan:indicators.labelPercentageImprovementPlansDemandsClosed")}
                                    </p>

                                    <h4 className="ml-3  mb-2 fw-bold">
                                        {data?.percentageImprovementPlansDemandsClosed || "-"} %
                                    </h4>
                                </div>
                            </Card>
                        </Col>
                        <Col>
                            <Card className="h-100 border-0 shadow-sm">
                                <div className="text-start">
                                    <div className="indicator-bar-light-red-horizontal" />

                                    <p className="ml-3 mt-2 text-muted mb-1 small">
                                        {t("improvementPlan:indicators.labelTotalFinalInverstment")}
                                    </p>

                                    <h4 className="ml-3  mb-2 fw-bold">
                                        {
                                            data?.totalFinalInverstment != null
                                                ? formatCurrency(data.totalFinalInverstment)
                                                : "-"
                                        }&euro;
                                    </h4>
                                </div>
                            </Card>
                        </Col>
                    
                    </Row>
                </Card >
            </div>
        </>
     );
}